<script setup lang="ts">
import 'viewerjs/dist/viewer.css'
import * as viewer from 'v-viewer'

import type { Size } from './common'

import FilePreview from './Preview.vue'
import type { UploadedFile } from '~/types'
import { internalRoles } from '~/authorization'

const props = withDefaults(defineProps<{
  allowDelete?: boolean
  files: UploadedFile[]
  size?: Size
}>(), {
  allowDelete: false,
  size: 'Normal',
})

const emit = defineEmits<{
  (e: 'delete', src: string): void
}>()

const { hasAnyAssignedRole } = useAuthorization()
const disableCookieConsent = hasAnyAssignedRole(internalRoles)

const pdfSource = ref<string | null>(null)
const videoSource = ref<string | null>(null)
const emailSource = ref<string | null>(null)

const images = computed(() => props.files.filter(it => it.type === 'image').map(it => it.filekey))

function showImages(startingFilePath: string) {
  const initialIndex = images.value.indexOf(startingFilePath)

  viewer.api({
    images: images.value,
    options: {
      toolbar: true,
      // Ensure the viewer is above other elements
      // https://github.com/sidestream-tech/hanselmann-os/pull/5192#discussion_r2200234631
      zIndex: 9999,
      initialViewIndex: Math.max(0, initialIndex), // convert -1 to 0 as `indexOf` may return -1
    },
  })
}
</script>

<template>
  <FileVideoPlayerPopup
    v-if="videoSource"
    :src="videoSource"
    :disable-cookie-consent
    @close="videoSource = null"
  />
  <LazyFilePdfEmbedPopup
    v-if="pdfSource"
    :source="pdfSource"
    @close="pdfSource = null"
  />
  <LazyFileEmailEmbedPopup
    v-if="emailSource"
    :source="emailSource"
    @close="emailSource = null"
  />

  <div class="images clearfix">
    <div class="flex flex-wrap gap-1">
      <FilePreview
        v-for="file in files"
        :key="file.filekey"
        :allow-delete-all="allowDelete"
        :file="file"
        :size="size"
        :disable-cookie-consent
        @show-image="showImages"
        @show-pdf="pdfSource = $event"
        @show-video="videoSource = $event"
        @show-email="emailSource = $event"
        @delete="emit('delete', $event)"
      />
    </div>
  </div>
</template>
